"use client";

import { useRef, useState } from "react";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { DECIBEL_CALCULATOR_STRINGS } from "@/components/tools/decibel-calculator";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import {
  RowList,
  type ValueRow,
} from "@/components/tools/physics-waves/row-list";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  addLevels,
  DB_PER_DOUBLING,
  dbmToWatts,
  dbToRatio,
  dbuToVolts,
  dbvToVolts,
  distanceForLevel,
  identicalSources,
  intensityFromLevel,
  levelAtDistance,
  levelFromIntensity,
  levelFromPressure,
  pressureFromLevel,
  type RatioKind,
  ratioToDb,
  subtractLevel,
  voltsToDbu,
  voltsToDbv,
  wattsToDbm,
  wattsToDbw,
} from "@/lib/tool-kit/physics-waves/decibel";
import {
  formatSig,
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  DECIBEL_UNITS,
  DISTANCE_UNITS,
  findUnit,
  INTENSITY_UNITS,
  PRESSURE_UNITS,
  toBase,
} from "@/lib/tool-kit/physics-waves/units";

export type DecibelStrings = typeof DECIBEL_CALCULATOR_STRINGS;

export interface PanelProps {
  strings: DecibelStrings;
  fmt: NumberFormatter;
  /** Decimal places shown for decibel values. */
  places: number;
}

interface Entry {
  value: string;
  unit: string;
}

const SIG = 4;

function db(fmt: NumberFormatter, value: number, places: number): string {
  return `${fmt(value, { minimumFractionDigits: places, maximumFractionDigits: places, useGrouping: false })} dB`;
}

function read(
  entry: Entry,
  units: Parameters<typeof findUnit>[0]
): number | null {
  const parsed = parseNumberInput(entry.value);
  return parsed === null ? null : toBase(parsed, findUnit(units, entry.unit));
}

function sci(fmt: NumberFormatter, value: number, unit: string): string {
  return `${formatSig(fmt, value, SIG)} ${unit}`;
}

function exposureTime(
  level: number,
  strings: DecibelStrings,
  fmt: NumberFormatter
): string {
  const hours = 8 / 2 ** ((level - 85) / 3);
  if (hours >= 1) {
    return t(strings.hoursValue, { value: formatSig(fmt, hours, 3) });
  }
  if (hours * 60 >= 1) {
    return t(strings.minutesValue, { value: formatSig(fmt, hours * 60, 3) });
  }
  return t(strings.secondsValue, { value: formatSig(fmt, hours * 3600, 3) });
}

function Section({
  strings,
  children,
}: {
  strings: DecibelStrings;
  children: React.ReactNode;
}) {
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {children}
    </section>
  );
}

type Known = "level" | "intensity" | "pressure";

export function SoundLevelPanel({ strings, fmt, places }: PanelProps) {
  const [known, setKnown] = useState<Known>("level");
  const [level, setLevel] = useState("85");
  const [intensity, setIntensity] = useState<Entry>({
    value: "316.2",
    unit: "uW/m²",
  });
  const [pressure, setPressure] = useState<Entry>({
    value: "0.3557",
    unit: "Pa",
  });
  let levelValue: number | null = null;
  if (known === "level") {
    levelValue = parseNumberInput(level);
  } else if (known === "intensity") {
    const i = read(intensity, INTENSITY_UNITS);
    levelValue = i !== null && i > 0 ? levelFromIntensity(i) : null;
  } else {
    const p = read(pressure, PRESSURE_UNITS);
    levelValue = p !== null && p > 0 ? levelFromPressure(p) : null;
  }
  return (
    <>
      <PillGroup
        label={strings.knownLabel}
        name="db-known"
        onChange={setKnown}
        options={(Object.keys(strings.knowns) as Known[]).map((value) => ({
          value,
          label: strings.knowns[value],
        }))}
        value={known}
      />
      <div className="max-w-sm">
        {known === "level" ? (
          <TextField
            id="db-level"
            label={strings.levelInput}
            numeric
            onChange={setLevel}
            value={level}
          />
        ) : null}
        {known === "intensity" ? (
          <QuantityField
            id="db-intensity"
            label={strings.intensityInput}
            onChange={(value) => setIntensity({ ...intensity, value })}
            onUnitChange={(unit) => setIntensity({ ...intensity, unit })}
            unit={intensity.unit}
            unitLabel={t(strings.unitFor, { name: strings.intensityInput })}
            units={INTENSITY_UNITS}
            value={intensity.value}
          />
        ) : null}
        {known === "pressure" ? (
          <QuantityField
            id="db-pressure"
            label={strings.pressureInput}
            onChange={(value) => setPressure({ ...pressure, value })}
            onUnitChange={(unit) => setPressure({ ...pressure, unit })}
            unit={pressure.unit}
            unitLabel={t(strings.unitFor, { name: strings.pressureInput })}
            units={PRESSURE_UNITS}
            value={pressure.value}
          />
        ) : null}
      </div>
      <Section strings={strings}>
        {levelValue === null ? <Notice>{strings.enterValue}</Notice> : null}
        {levelValue === null ? null : (
          <>
            <StatGrid>
              <StatCard
                emphasis
                label={strings.levelResult}
                value={db(fmt, levelValue, places)}
              />
              <StatCard
                label={strings.intensityResult}
                value={sci(fmt, intensityFromLevel(levelValue), "W/m²")}
              />
              <StatCard
                label={strings.pressureResult}
                value={withSiPrefix(
                  fmt,
                  pressureFromLevel(levelValue),
                  "Pa",
                  SIG
                )}
              />
              <StatCard
                label={strings.ratioResult}
                value={sci(fmt, 10 ** (levelValue / 10), "×")}
              />
            </StatGrid>
            <WorkingSteps
              heading={strings.working}
              lines={[
                t(strings.stepIntensity, {
                  i: sci(fmt, intensityFromLevel(levelValue), "W/m²"),
                  level: db(fmt, levelValue, places),
                }),
                t(strings.stepPressure, {
                  p: withSiPrefix(
                    fmt,
                    pressureFromLevel(levelValue),
                    "Pa",
                    SIG
                  ),
                  level: db(fmt, levelValue, places),
                }),
              ]}
            />
            {levelValue >= 85 ? (
              <Notice>
                {t(strings.exposureNote, {
                  level: db(fmt, levelValue, places),
                  time: exposureTime(levelValue, strings, fmt),
                })}
              </Notice>
            ) : null}
          </>
        )}
      </Section>
    </>
  );
}

type Combine = "add" | "subtract" | "identical";

function AddResult({
  values,
  strings,
  fmt,
  places,
}: PanelProps & { values: number[] }) {
  const total = addLevels(values);
  if (total === null) {
    return <Notice>{strings.enterTwoLevels}</Notice>;
  }
  const loudest = Math.max(...values);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.totalLevel}
          value={db(fmt, total, places)}
        />
        <StatCard
          label={strings.aboveLoudest}
          value={db(fmt, total - loudest, places)}
        />
      </StatGrid>
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(strings.stepAdd, {
            terms: values
              .map((v) => `10^(${formatSig(fmt, v, SIG)}/10)`)
              .join(" + "),
            total: db(fmt, total, places),
          }),
        ]}
      />
    </>
  );
}

function SubtractResult({
  total,
  background,
  strings,
  fmt,
  places,
}: PanelProps & { total: number | null; background: number | null }) {
  if (total === null || background === null) {
    return <Notice>{strings.enterValue}</Notice>;
  }
  const remaining = subtractLevel(total, background);
  if (remaining === null) {
    return <ErrorNotice>{strings.backgroundTooLoud}</ErrorNotice>;
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.sourceLevel}
          value={db(fmt, remaining, places)}
        />
        <StatCard
          label={strings.correction}
          value={db(fmt, remaining - total, places)}
        />
      </StatGrid>
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(strings.stepSubtract, {
            total: db(fmt, total, places),
            background: db(fmt, background, places),
            source: db(fmt, remaining, places),
          }),
        ]}
      />
    </>
  );
}

function IdenticalResult({
  level,
  count,
  strings,
  fmt,
  places,
}: PanelProps & { level: number | null; count: number | null }) {
  if (level === null || count === null || count < 1) {
    return <Notice>{strings.enterValue}</Notice>;
  }
  const total = identicalSources(level, count);
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.totalLevel}
          value={db(fmt, total, places)}
        />
        <StatCard
          label={strings.aboveSingle}
          value={db(fmt, identicalSources(0, count), places)}
        />
      </StatGrid>
      <WorkingSteps
        heading={strings.working}
        lines={[
          t(strings.stepIdentical, {
            level: db(fmt, level, places),
            n: formatSig(fmt, count, SIG),
            total: db(fmt, total, places),
          }),
        ]}
      />
    </>
  );
}

export function CombinePanel({ strings, fmt, places }: PanelProps) {
  const [mode, setMode] = useState<Combine>("add");
  const [rows, setRows] = useState<ValueRow[]>([
    { id: 1, value: "85", unit: "dB" },
    { id: 2, value: "82", unit: "dB" },
    { id: 3, value: "79", unit: "dB" },
  ]);
  const [total, setTotal] = useState("70");
  const [background, setBackground] = useState("60");
  const [single, setSingle] = useState("60");
  const [count, setCount] = useState("10");
  const nextId = useRef(100);
  const values = rows
    .map((row) => parseNumberInput(row.value))
    .filter((value): value is number => value !== null);
  const totalValue = parseNumberInput(total);
  const backgroundValue = parseNumberInput(background);
  const singleValue = parseNumberInput(single);
  const countValue = parseNumberInput(count);
  const rowStrings = {
    rowLabel: strings.levelRow,
    unitFor: strings.unitFor,
    remove: strings.removeRow,
    add: strings.addRow,
  };

  return (
    <>
      <PillGroup
        label={strings.combineLabel}
        name="db-combine"
        onChange={setMode}
        options={(Object.keys(strings.combines) as Combine[]).map((value) => ({
          value,
          label: strings.combines[value],
        }))}
        value={mode}
      />
      {mode === "add" ? (
        <RowList
          defaultUnit="dB"
          idPrefix="db-row"
          nextId={() => {
            nextId.current += 1;
            return nextId.current;
          }}
          onChange={setRows}
          rows={rows}
          strings={rowStrings}
          units={DECIBEL_UNITS}
        />
      ) : null}
      {mode === "subtract" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="db-total"
            label={strings.measuredTotal}
            numeric
            onChange={setTotal}
            value={total}
          />
          <TextField
            id="db-background"
            label={strings.backgroundLevel}
            numeric
            onChange={setBackground}
            value={background}
          />
        </div>
      ) : null}
      {mode === "identical" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="db-single"
            label={strings.singleLevel}
            numeric
            onChange={setSingle}
            value={single}
          />
          <TextField
            id="db-count"
            label={strings.sourceCount}
            numeric
            onChange={setCount}
            value={count}
          />
        </div>
      ) : null}
      <Section strings={strings}>
        {mode === "add" ? (
          <AddResult
            fmt={fmt}
            places={places}
            strings={strings}
            values={values}
          />
        ) : null}
        {mode === "subtract" ? (
          <SubtractResult
            background={backgroundValue}
            fmt={fmt}
            places={places}
            strings={strings}
            total={totalValue}
          />
        ) : null}
        {mode === "identical" ? (
          <IdenticalResult
            count={countValue}
            fmt={fmt}
            level={singleValue}
            places={places}
            strings={strings}
          />
        ) : null}
      </Section>
    </>
  );
}

type Goal = "level" | "distance";
const MULTIPLES = [2, 4, 10, 100];

export function DistancePanel({ strings, fmt, places }: PanelProps) {
  const [goal, setGoal] = useState<Goal>("level");
  const [level1, setLevel1] = useState("90");
  const [r1, setR1] = useState<Entry>({ value: "1", unit: "m" });
  const [r2, setR2] = useState<Entry>({ value: "10", unit: "m" });
  const [level2, setLevel2] = useState("60");
  const l1 = parseNumberInput(level1);
  const d1 = read(r1, DISTANCE_UNITS);
  const d2 = read(r2, DISTANCE_UNITS);
  const l2 = parseNumberInput(level2);
  const ready =
    l1 !== null &&
    d1 !== null &&
    d1 > 0 &&
    (goal === "level" ? d2 !== null && d2 > 0 : l2 !== null);
  const show = (metres: number) => withSiPrefix(fmt, metres, "m", SIG);
  let content = <Notice>{strings.enterValue}</Notice>;
  if (ready) {
    const finalLevel =
      goal === "level" ? levelAtDistance(l1, d1, d2 ?? d1) : (l2 ?? 0);
    const finalDistance =
      goal === "level" ? (d2 ?? d1) : distanceForLevel(l1, d1, l2 ?? 0);
    content = (
      <>
        <StatGrid>
          <StatCard
            emphasis
            label={
              goal === "level"
                ? strings.levelAtDistance
                : strings.distanceForLevel
            }
            value={
              goal === "level"
                ? db(fmt, finalLevel, places)
                : show(finalDistance)
            }
          />
          <StatCard
            label={strings.levelChange}
            value={db(fmt, finalLevel - l1, places)}
          />
          <StatCard
            label={strings.perDoubling}
            value={db(fmt, DB_PER_DOUBLING, places)}
          />
        </StatGrid>
        <WorkingSteps
          heading={strings.working}
          lines={[
            t(strings.stepDistance, {
              l1: db(fmt, l1, places),
              r1: show(d1),
              r2: show(finalDistance),
              l2: db(fmt, finalLevel, places),
            }),
          ]}
        />
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.falloffHeading}</h3>
          <DataTable
            head={[strings.columnDistance, strings.columnLevel]}
            rows={MULTIPLES.map((m) => [
              show(d1 * m),
              db(fmt, levelAtDistance(l1, d1, d1 * m), places),
            ])}
          />
        </div>
      </>
    );
  }
  return (
    <>
      <PillGroup
        label={strings.distanceGoalLabel}
        name="db-distance-goal"
        onChange={setGoal}
        options={[
          { value: "level", label: strings.goalLevel },
          { value: "distance", label: strings.goalDistance },
        ]}
        value={goal}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="db-l1"
          label={strings.knownLevel}
          numeric
          onChange={setLevel1}
          value={level1}
        />
        <QuantityField
          id="db-r1"
          label={strings.knownDistance}
          onChange={(value) => setR1({ ...r1, value })}
          onUnitChange={(unit) => setR1({ ...r1, unit })}
          unit={r1.unit}
          unitLabel={t(strings.unitFor, { name: strings.knownDistance })}
          units={DISTANCE_UNITS}
          value={r1.value}
        />
        {goal === "level" ? (
          <QuantityField
            id="db-r2"
            label={strings.newDistance}
            onChange={(value) => setR2({ ...r2, value })}
            onUnitChange={(unit) => setR2({ ...r2, unit })}
            unit={r2.unit}
            unitLabel={t(strings.unitFor, { name: strings.newDistance })}
            units={DISTANCE_UNITS}
            value={r2.value}
          />
        ) : (
          <TextField
            id="db-l2"
            label={strings.targetLevel}
            numeric
            onChange={setLevel2}
            value={level2}
          />
        )}
      </div>
      <Section strings={strings}>{content}</Section>
      <p className="text-muted-foreground text-xs">{strings.freeFieldNote}</p>
    </>
  );
}

type Direction = "toDb" | "toRatio";

function gainValues(
  direction: Direction,
  kind: RatioKind,
  ratio: number | null,
  gain: number | null
): { dbValue: number | null; ratioValue: number | null } {
  if (direction === "toDb") {
    const valid = ratio !== null && ratio > 0;
    return {
      dbValue: valid ? ratioToDb(ratio, kind) : null,
      ratioValue: ratio,
    };
  }
  return {
    dbValue: gain,
    ratioValue: gain === null ? null : dbToRatio(gain, kind),
  };
}

export function GainPanel({ strings, fmt, places }: PanelProps) {
  const [kind, setKind] = useState<RatioKind>("power");
  const [direction, setDirection] = useState<Direction>("toDb");
  const [ratio, setRatio] = useState("2");
  const [gain, setGain] = useState("3");
  const r = parseNumberInput(ratio);
  const g = parseNumberInput(gain);
  const factor = kind === "power" ? 10 : 20;
  const values = gainValues(direction, kind, r, g);
  const { dbValue, ratioValue } = values;
  const valid = dbValue !== null && ratioValue !== null && ratioValue > 0;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <PillGroup
          label={strings.ratioKindLabel}
          name="db-ratio-kind"
          onChange={setKind}
          options={[
            { value: "power", label: strings.kindPower },
            { value: "amplitude", label: strings.kindAmplitude },
          ]}
          value={kind}
        />
        <PillGroup
          label={strings.directionLabel}
          name="db-direction"
          onChange={setDirection}
          options={[
            { value: "toDb", label: strings.directionToDb },
            { value: "toRatio", label: strings.directionToRatio },
          ]}
          value={direction}
        />
      </div>
      <div className="max-w-sm">
        {direction === "toDb" ? (
          <TextField
            id="db-ratio"
            label={strings.ratioInput}
            numeric
            onChange={setRatio}
            value={ratio}
          />
        ) : (
          <TextField
            id="db-gain"
            label={strings.gainInput}
            numeric
            onChange={setGain}
            value={gain}
          />
        )}
      </div>
      <Section strings={strings}>
        {valid ? (
          <>
            <StatGrid>
              <StatCard
                emphasis={direction === "toDb"}
                label={strings.gainResult}
                value={db(fmt, dbValue, places)}
              />
              <StatCard
                emphasis={direction === "toRatio"}
                label={strings.ratioResult2}
                value={formatSig(fmt, ratioValue, SIG)}
              />
              <StatCard
                label={strings.percentChange}
                value={`${ratioValue >= 1 ? "+" : ""}${formatSig(fmt, (kind === "power" ? ratioValue - 1 : ratioValue - 1) * 100, SIG)}%`}
              />
            </StatGrid>
            <WorkingSteps
              heading={strings.working}
              lines={[
                t(strings.stepGain, {
                  factor,
                  ratio: formatSig(fmt, ratioValue, SIG),
                  db: db(fmt, dbValue, places),
                }),
              ]}
            />
          </>
        ) : (
          <Notice>{strings.enterPositive}</Notice>
        )}
      </Section>
    </>
  );
}

type AbsoluteKind = "power" | "voltage";

const POWER_UNITS_ABS = [
  { id: "dBm", symbol: "dBm", exp: 0 },
  { id: "dBW", symbol: "dBW", exp: 0 },
  { id: "mW", symbol: "mW", exp: 0 },
  { id: "W", symbol: "W", exp: 0 },
];
const VOLTAGE_UNITS_ABS = [
  { id: "dBV", symbol: "dBV", exp: 0 },
  { id: "dBu", symbol: "dBu", exp: 0 },
  { id: "mV", symbol: "mV", exp: 0 },
  { id: "V", symbol: "V", exp: 0 },
];

function toWatts(value: number, unit: string): number {
  if (unit === "dBm") {
    return dbmToWatts(value);
  }
  if (unit === "dBW") {
    return 10 ** (value / 10);
  }
  return unit === "mW" ? value / 1000 : value;
}

function toVolts(value: number, unit: string): number {
  if (unit === "dBV") {
    return dbvToVolts(value);
  }
  if (unit === "dBu") {
    return dbuToVolts(value);
  }
  return unit === "mV" ? value / 1000 : value;
}

export function AbsolutePanel({ strings, fmt, places }: PanelProps) {
  const [kind, setKind] = useState<AbsoluteKind>("power");
  const [power, setPower] = useState<Entry>({ value: "20", unit: "dBm" });
  const [voltage, setVoltage] = useState<Entry>({ value: "4", unit: "dBu" });
  const entry = kind === "power" ? power : voltage;
  const typed = parseNumberInput(entry.value);
  const isLog = entry.unit.startsWith("dB");
  let base: number | null = null;
  if (typed !== null) {
    base =
      kind === "power"
        ? toWatts(typed, entry.unit)
        : toVolts(typed, entry.unit);
  }
  const valid = base !== null && base > 0 && (isLog || typed !== null);
  return (
    <>
      <PillGroup
        label={strings.absoluteKindLabel}
        name="db-absolute-kind"
        onChange={setKind}
        options={[
          { value: "power", label: strings.absolutePower },
          { value: "voltage", label: strings.absoluteVoltage },
        ]}
        value={kind}
      />
      <div className="max-w-sm">
        <QuantityField
          id="db-absolute"
          label={
            kind === "power" ? strings.absolutePower : strings.absoluteVoltage
          }
          onChange={(value) =>
            kind === "power"
              ? setPower({ ...power, value })
              : setVoltage({ ...voltage, value })
          }
          onUnitChange={(unit) =>
            kind === "power"
              ? setPower({ ...power, unit })
              : setVoltage({ ...voltage, unit })
          }
          unit={entry.unit}
          unitLabel={t(strings.unitFor, {
            name:
              kind === "power"
                ? strings.absolutePower
                : strings.absoluteVoltage,
          })}
          units={kind === "power" ? POWER_UNITS_ABS : VOLTAGE_UNITS_ABS}
          value={entry.value}
        />
      </div>
      <Section strings={strings}>
        {valid && base !== null ? (
          <StatGrid>
            {kind === "power" ? (
              <>
                <StatCard
                  emphasis
                  label="dBm"
                  value={fmt(wattsToDbm(base), {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                    useGrouping: false,
                  })}
                />
                <StatCard
                  label="dBW"
                  value={fmt(wattsToDbw(base), {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                    useGrouping: false,
                  })}
                />
                <StatCard label="mW" value={formatSig(fmt, base * 1000, SIG)} />
                <StatCard label="W" value={formatSig(fmt, base, SIG)} />
              </>
            ) : (
              <>
                <StatCard
                  emphasis
                  label="dBV"
                  value={fmt(voltsToDbv(base), {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                    useGrouping: false,
                  })}
                />
                <StatCard
                  label="dBu"
                  value={fmt(voltsToDbu(base), {
                    minimumFractionDigits: places,
                    maximumFractionDigits: places,
                    useGrouping: false,
                  })}
                />
                <StatCard label="mV" value={formatSig(fmt, base * 1000, SIG)} />
                <StatCard label="V" value={formatSig(fmt, base, SIG)} />
              </>
            )}
          </StatGrid>
        ) : (
          <Notice>{strings.enterPositive}</Notice>
        )}
      </Section>
      <p className="text-muted-foreground text-xs">{strings.absoluteNote}</p>
    </>
  );
}
